// Load before anything else
import 'src/lib/preload';

import type { AppProps } from 'next/app';
import { I18nProvider } from 'next-localization';
import { ErrorBoundary } from 'react-error-boundary';
import { SitecorePageProps } from 'lib/page-props';
import Bootstrap from 'src/Bootstrap';
import { Render500Fallback } from 'src/Render500Fallback';

import 'assets/app.scss';
import 'src/assets/themes/index.css';
import { supportedFonts } from 'lib/fonts';
import { useEffect, useLayoutEffect, useMemo } from 'react';
import { SessionProvider } from 'next-auth/react';
import {
  clearPendingSearchRouteAnnouncement,
  peekPendingSearchRouteAnnouncement,
  rememberSearchRouteAnnouncement,
} from 'lib/a11y/searchRouteAnnouncement';
import { SpeedInsights } from '@vercel/speed-insights/next';

const IOS_MOBILE_INPUT_FIX_CLASS = 'ios-mobile-input-fix';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function deepen(obj: Record<string, any>) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const result: Record<string, any> = {};

  // For each object path (property key) in the object
  for (const objectPath in obj) {
    // Split path into component parts
    const parts = objectPath.split('.');

    // Create sub-objects along path as needed
    let target = result;
    while (parts.length > 1) {
      const part = parts.shift();
      if (!part) continue;
      target = target[part] = target[part] || {};
    }

    // Set value at end of path
    target[parts[0]] = obj[objectPath];
  }

  return result;
}

const isSearchRouteUrl = (url: string) => url === '/search' || url.startsWith('/search?');
const SEARCH_SUMMARY_AFTER_TITLE_DELAY_MS = 400;

function App({ Component, pageProps, router }: AppProps<SitecorePageProps>): JSX.Element {
  const { dictionary, ...rest } = pageProps;

  // Memoize deepen() so I18nProvider receives a stable lngDict reference.
  // Without useMemo this creates a new object every render, causing all
  // i18n consumers to re-render unnecessarily.
  const lngDict = useMemo(() => deepen(dictionary || {}), [dictionary]);

  // Fix Next.js route announcer announcing stale document.title before <Head> updates it.
  // display:none removes it from the a11y tree; our own live region announces the correct title.
  useEffect(() => {
    // Hide Next's announcer from the a11y tree so NVDA never receives the stale mutation
    const silenceBuiltInAnnouncer = () => {
      const el = document.getElementById('__next-route-announcer__') as HTMLElement | null;
      if (el && el.style.display !== 'none') el.style.display = 'none';
    };
    silenceBuiltInAnnouncer();
    // Re-run if announcer mounts after this effect (hydration timing), then stop watching
    const bodyObserver = new MutationObserver(() => {
      const el = document.getElementById('__next-route-announcer__') as HTMLElement | null;
      if (el) {
        if (el.style.display !== 'none') el.style.display = 'none';
        bodyObserver.disconnect(); // stop watching once announcer is found and silenced
      }
    });
    bodyObserver.observe(document.body, { childList: true, subtree: false });

    // Our own visually-hidden live region (no role="alert" to avoid "Alert!" prefix in NVDA)
    const liveRegion = document.createElement('p');
    liveRegion.setAttribute('aria-live', 'assertive');
    Object.assign(liveRegion.style, {
      border: '0',
      clip: 'rect(0 0 0 0)',
      height: '1px',
      margin: '-1px',
      overflow: 'hidden',
      padding: '0',
      position: 'absolute',
      top: '0',
      width: '1px',
      whiteSpace: 'nowrap',
    });
    document.body.appendChild(liveRegion);

    const announce = (text: string) => {
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = text;
      });
    };

    let pendingSearchRouteAnnouncement: string | null = null;

    // Primary: announce when document.title genuinely changes after navigation
    let prevTitle = document.title;
    let lastAnnouncedTitle = document.title;
    let titleChangedDuringNav = false;
    const titleObserver = new MutationObserver(() => {
      if (!document.title || document.title === prevTitle) return;
      prevTitle = document.title;

      titleChangedDuringNav = true;
      lastAnnouncedTitle = document.title;
      announce(document.title);
    });
    titleObserver.observe(document.head, { childList: true, subtree: true, characterData: true });

    // Reset the flag at the start of every navigation so the fallback guard is accurate
    const onRouteStart = (url: string) => {
      titleChangedDuringNav = false;
      pendingSearchRouteAnnouncement = isSearchRouteUrl(url)
        ? peekPendingSearchRouteAnnouncement()
        : null;
    };

    // Fallback: announce on routeChangeComplete only if titleObserver hasn't already fired
    // AND the title actually changed from what we last announced (covers two-page same-title case,
    // while avoiding re-reading the title on same-page shallow navigation like /search?term=X).
    const onRouteComplete = () => {
      setTimeout(() => {
        const resolvedSearchRouteAnnouncement =
          pendingSearchRouteAnnouncement ??
          (isSearchRouteUrl(router.asPath) ? peekPendingSearchRouteAnnouncement() : null);

        if (!titleChangedDuringNav && document.title && document.title !== lastAnnouncedTitle) {
          lastAnnouncedTitle = document.title;
          announce(document.title);
        }

        if (resolvedSearchRouteAnnouncement) {
          rememberSearchRouteAnnouncement(resolvedSearchRouteAnnouncement);
          clearPendingSearchRouteAnnouncement();
          pendingSearchRouteAnnouncement = null;
          window.setTimeout(() => {
            announce(resolvedSearchRouteAnnouncement);
          }, SEARCH_SUMMARY_AFTER_TITLE_DELAY_MS);
        }
      }, 300);
    };

    const onRouteError = () => {
      pendingSearchRouteAnnouncement = null;
    };

    router.events.on('routeChangeStart', onRouteStart);
    router.events.on('routeChangeComplete', onRouteComplete);
    router.events.on('routeChangeError', onRouteError);

    return () => {
      bodyObserver.disconnect();
      titleObserver.disconnect();
      router.events.off('routeChangeStart', onRouteStart);
      router.events.off('routeChangeComplete', onRouteComplete);
      router.events.off('routeChangeError', onRouteError);
      if (document.body.contains(liveRegion)) document.body.removeChild(liveRegion);
    };
  }, [router.events]);

  // Use LayoutEffect to ensure the body classes are applied immediately after the component is mounted
  useLayoutEffect(() => {
    const root = document.documentElement;

    const syncIosMobileClass = () => {
      const isAppleMobileDevice =
        /iPhone|iPad|iPod/.test(window.navigator.userAgent) ||
        (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
      const isMobileViewport = window.matchMedia('(max-width: 767px)').matches;

      root.classList.toggle(
        IOS_MOBILE_INPUT_FIX_CLASS,
        isAppleMobileDevice && window.navigator.maxTouchPoints > 0 && isMobileViewport
      );
    };

    // Add the supported fonts to the body to ensure they are available for the entire page
    document.body.classList.add(...supportedFonts.map((font) => font.variable));

    syncIosMobileClass();
    window.addEventListener('resize', syncIosMobileClass);

    return () => {
      window.removeEventListener('resize', syncIosMobileClass);
      root.classList.remove(IOS_MOBILE_INPUT_FIX_CLASS);
    };
  }, []);

  return (
    <>
      <Bootstrap {...pageProps} />
      <SpeedInsights />
      {/*
        // Use the next-localization (w/ rosetta) library to provide our translation dictionary to the app.
        // Note Next.js does not (currently) provide anything for translation, only i18n routing.
        // If your app is not multilingual, next-localization and references to it can be removed.
      */}
      <I18nProvider lngDict={lngDict} locale={pageProps.page?.locale || ''}>
        <ErrorBoundary
          fallbackRender={({ resetErrorBoundary, error }) => (
            <Render500Fallback
              router={router}
              locale={pageProps.page?.locale || ''}
              Component={Component}
              error={error}
              resetErrorBoundary={resetErrorBoundary}
            />
          )}
        >
          <SessionProvider session={pageProps.session}>
            <Component {...rest} />
          </SessionProvider>
        </ErrorBoundary>
      </I18nProvider>
    </>
  );
}

export default App;
